import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import {
  DefaultRequest,
  NoticeListRequestType,
  BusinessNoticeSelectListResponse,
  BusinessNoticeSendRequest,
  BusinessNoticeSendResponse,
  BusinessNoticeAlbumListRequest,
  BusinessNoticeAlbumListResponse,
} from "~/types/common";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface BusinessNoticeDataType {
  type: string;
  message: string;
}

export const BUSINESS_NOTICE_QUERY_KEYS = {
  notice: ["business", "notice"],
  recipientList: ["business", "notice", "recipient-list"],
  sendRecipientList: ["business", "notice", "send-recipient-list"],
  senderList: ["business", "notice", "sender-list"],
  tempList: ["business", "notice", "temp-list"],
  newList: ["business", "notice", "new-list"],
  sendSubData: ["business", "notice", "send-sub-data"],
  saveList: ["business", "notice", "save-list"],
  selectList: ["business", "notice", "select-list"],
} as const;

// 받은 알림 목록 조회
const getBusinessNoticeRecipientList = async (
  request: NoticeListRequestType,
) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_RECIPIENT_LIST,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 받은 알림 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeRecipientList = (
  request: NoticeListRequestType,
) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.recipientList, request],
    queryFn: () => getBusinessNoticeRecipientList(request),
    select: (data) => data,
  });
};

// 알림 전송용 수신자 목록 조회 (동일한 엔드포인트, 다른 용도)
const getBusinessNoticeSendRecipientList = async (
  request: NoticeListRequestType,
) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_RECIPIENT_LIST,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 알림 전송용 수신자 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeSendRecipientList = (
  request: NoticeListRequestType,
) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.sendRecipientList, request],
    queryFn: () => getBusinessNoticeSendRecipientList(request),
    select: (data) => data,
  });
};

// 보낸 알림 목록 조회
const getBusinessNoticeSenderList = async (request: NoticeListRequestType) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_SENDER_LIST,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 보낸 알림 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeSenderList = (request: NoticeListRequestType) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.senderList, request],
    queryFn: () => getBusinessNoticeSenderList(request),
    select: (data) => data,
  });
};

// 임시저장 알림 목록 조회
const getBusinessNoticeTempList = async (request: NoticeListRequestType) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_TEMP_LIST,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 임시저장 알림 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeTempList = (request: NoticeListRequestType) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.tempList, request],
    queryFn: () => getBusinessNoticeTempList(request),
    select: (data) => data,
  });
};

// 새 알림 목록 조회
// const getBusinessNewNoticeList = async (request: DefaultRequest) => {
//   try {
//     const { data } = await privateHttp.get(
//       API_ENDPOINTS.BUSINESS_NOTICE.NEW_NOTICE_LIST,
//       { params: request }
//     );
//     return data.data;
//   } catch (error) {
//     console.error("비즈니스 새 알림 목록 API 호출 에러:", error);
//     throw error;
//   }
// };

// export const useBusinessNewNoticeList = (request: DefaultRequest) => {
//   return useQuery({
//     queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.newList, request],
//     queryFn: () => getBusinessNewNoticeList(request),
//     select: (data) => data,
//   });
// };

// 알림 전송용 서브 데이터 조회
const getBusinessNoticeSendSubData = async (request: DefaultRequest) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_SEND_SUBDATA,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 알림 전송 서브 데이터 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeSendSubData = (request: DefaultRequest) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.sendSubData, request],
    queryFn: () => getBusinessNoticeSendSubData(request),
    select: (data) => data,
  });
};

// 저장된 알림 목록 조회
const getBusinessNoticeSaveList = async (request: DefaultRequest) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_SAVE_LIST,
      { params: request },
    );
    return data.data;
  } catch (error) {
    console.error("비즈니스 저장된 알림 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeSaveList = (request: DefaultRequest) => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.saveList, request],
    queryFn: () => getBusinessNoticeSaveList(request),
    select: (data) => data,
  });
};

// 알림 전송 폼 선택 목록 조회
const getBusinessNoticeSelectList =
  async (): Promise<BusinessNoticeSelectListResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_SELECT_LIST,
        { params: { bizUserSeq: 69 } },
      );
      return data.data;
    } catch (error) {
      console.error("비즈니스 알림 전송 폼 선택 목록 API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessNoticeSelectList = () => {
  return useQuery({
    queryKey: [...BUSINESS_NOTICE_QUERY_KEYS.selectList],
    queryFn: () => getBusinessNoticeSelectList(),
    select: (data) => data,
  });
};

// 알림 전송
export const sendBusinessNotice = async (
  request: BusinessNoticeSendRequest,
): Promise<BusinessNoticeSendResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_SEND,
      request,
    );
    return data;
  } catch (error) {
    console.error("비즈니스 알림 전송 API 호출 에러:", error);
    throw error;
  }
};

export const useSendBusinessNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendBusinessNotice,
    onSuccess: (data) => {
      console.log("알림 전송 성공:", data);
      // 관련 쿼리 무효화 (필요시)
      queryClient.invalidateQueries({
        queryKey: BUSINESS_NOTICE_QUERY_KEYS.senderList,
      });
    },
    onError: (error) => {
      console.error("알림 전송 실패:", error);
    },
  });
};

// 발송된 알림장 수정 (오탈자 정정 등) — 푸시 재발송 없이 내용만 갱신
const editSentBusinessNotice = async (
  request: BusinessNoticeSendRequest,
): Promise<BusinessNoticeSendResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_EDIT,
      request,
    );
    return data;
  } catch (error) {
    console.error("비즈니스 알림장 수정 API 호출 에러:", error);
    throw error;
  }
};

// 저장 알림장(임시저장·예약 미발송) 삭제
export const deleteTempBusinessNotice = async (
  noticeSeq: number,
): Promise<BusinessNoticeSendResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_TEMP_DELETE,
      { noticeSeq },
    );
    return data;
  } catch (error) {
    console.error("저장 알림장 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useDeleteTempBusinessNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTempBusinessNotice,
    onSuccess: () => {
      // 저장 목록/상세 갱신
      queryClient.invalidateQueries({
        queryKey: BUSINESS_NOTICE_QUERY_KEYS.tempList,
      });
      queryClient.invalidateQueries({
        queryKey: ["business-notice-temp-detail"],
      });
    },
  });
};

export const useEditSentBusinessNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editSentBusinessNotice,
    onSuccess: (data) => {
      console.log("알림장 수정 성공:", data);
      // 발신 목록/상세 갱신
      queryClient.invalidateQueries({
        queryKey: BUSINESS_NOTICE_QUERY_KEYS.senderList,
      });
      queryClient.invalidateQueries({
        queryKey: ["business-notice-temp-detail"],
      });
    },
    onError: (error) => {
      console.error("알림장 수정 실패:", error);
    },
  });
};

// 앨범 목록 조회
export const getBusinessNoticeAlbumList = async (
  request: BusinessNoticeAlbumListRequest,
): Promise<BusinessNoticeAlbumListResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_NOTICE.ALBUM_LIST,
      request,
    );
    return data;
  } catch (error) {
    console.error("비즈니스 앨범 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessNoticeAlbumList = () => {
  return useMutation({
    mutationFn: getBusinessNoticeAlbumList,
    onSuccess: (data) => {
      console.log("앨범 목록 조회 성공:", data);
    },
    onError: (error) => {
      console.error("앨범 목록 조회 실패:", error);
    },
  });
};
